import {
  ApplicationConfig,
  APP_INITIALIZER,
  EnvironmentProviders,
  inject,
  InjectionToken,
  PLATFORM_ID,
  Provider,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, ROUTES, Routes, UrlSerializer, withInMemoryScrolling } from '@angular/router';
import { CustomeUrlSerializer } from './shared/utilities/url.serializer';

import { routes, aeRoutes } from './app.routes';
import {
  provideClientHydration,
  withEventReplay,
  withIncrementalHydration,
} from '@angular/platform-browser';
import { provideHttpClient, withInterceptors, HttpInterceptorFn } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { IMAGE_LOADER, isPlatformBrowser, Location } from '@angular/common';
import { myCustomLoader } from './customImageLoader';
import { AppHttpInterceptorInterceptor } from './shared/interceptors/app-http-interceptor.interceptor';
import { AWSInterceptor } from './shared/interceptors/aws-waf.interceptor';
import { PageTrackingService } from './shared/services/page-tracking.service';

const appHttpInterceptorFn: HttpInterceptorFn = (req, next) => {
  const interceptor = new AppHttpInterceptorInterceptor();

  const handler = {
    handle: next
  };
  return interceptor.intercept(req, handler);
};

const awsInterceptorFn: HttpInterceptorFn = (req, next) => {
  const interceptor = new AWSInterceptor();

  const handler = {
    handle: next
  };
  return interceptor.intercept(req, handler);
};

const aeRoutesHostnames = [
  'www.vertofx.ae',
  // 'localhost'
];

export const BROWSER_WINDOW = new InjectionToken<Window>(
  'Global window object',
  {
    // Make this token available throughout the application
    providedIn: 'root',
    factory: () => {
      // Check if 'window' is defined (i.e., we are in a browser environment)
      if (typeof window !== 'undefined') {
        return window;
      }
      // Return an empty object (cast to Window) for SSR to prevent 'undefined' errors
      return {} as Window;
    },
  }
);
function provideDynamicRouter(): EnvironmentProviders {
  // Inject PLATFORM_ID to determine if the application is running in a browser
  const platformId = inject(PLATFORM_ID);
  // Inject our custom BROWSER_WINDOW token to get the window object safely
  const browserWindow = inject(BROWSER_WINDOW);

  // Check if the application is running in a browser
  if (isPlatformBrowser(platformId)) {
    // Access hostname only if in a browser; use optional chaining for extra safety
    const hostname = browserWindow.location?.hostname;

    // If the hostname is one of the AE-specific hostnames, provide aeRoutes
    if (hostname && aeRoutesHostnames.includes(hostname)) {
      return provideRouter(aeRoutes);
    }
  }

  // Otherwise (for server-side rendering or other hostnames), provide default routes
  return provideRouter(routes);
}
export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withInterceptors([
      // appHttpInterceptorFn,
      awsInterceptorFn
    ])), // Provides HttpClient with interceptors
    provideZoneChangeDetection({ eventCoalescing: true }), // Configures Zone.js change detection
    provideAnimations(), // Provides browser animations
    {
      provide: IMAGE_LOADER,
      useValue: myCustomLoader,
    },
    {
      provide: UrlSerializer,
      useClass: CustomeUrlSerializer,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (pageTracking: PageTrackingService) => {
        // Initialize the PageTrackingService on app startup
        // The service will automatically start tracking via its constructor
        return () => pageTracking;
      },
      deps: [PageTrackingService],
      multi: true,
    },
    {
      provide: ROUTES,
      useFactory: (platformId: Object, browserWindow: Window): Routes => {
        let selectedRoutes: Routes;
        
        if (isPlatformBrowser(platformId)) {
          const hostname = browserWindow.location?.hostname;
          if (hostname && aeRoutesHostnames.includes(hostname)) {
            selectedRoutes = aeRoutes;
          } else {
            selectedRoutes = routes;
          }
        } else {
          selectedRoutes = [...routes, ...aeRoutes];
        }
        return selectedRoutes;
      },
      deps: [PLATFORM_ID, BROWSER_WINDOW],
      multi: true,
    },
    provideRouter([], withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' })),
    provideClientHydration(withIncrementalHydration()), // Enables Angular's client-side hydration
  ],
};
